import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API, th, td, inp, money, Modal, StatPill } from "./shared";

const RATES = [0, 5, 12, 18, 28, 40];
const effRate = (i) => i.gst_pct_effective ?? i.gst_pct;

export function GstFixModal({ invoice, close, onDone }) {
  const [rate, setRate] = useState(RATES.find(r => r !== effRate(invoice)));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const taxable = (invoice.line_items || []).filter(l => l.hsn_sac !== "996812").reduce((s, l) => s + (l.taxable_value_inr || 0), 0);
  const delta = Math.round(taxable * (rate - effRate(invoice))) / 100;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/${invoice.id}/gst-correction`, { new_gst_rate_pct: rate, reason });
      toast.success(`${data.notes.length} note${data.notes.length === 1 ? "" : "s"} issued: ${data.notes.map(n => n.note_number).join(", ")}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue note");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Correct GST rate" close={close}>
      <div data-testid="gst-fix-modal" style={{display:"grid",gap:12,padding:"4px 2px"}}>
        <p style={{fontSize:12.5,color:"#475569",margin:0}}>
          <b>{invoice.invoice_number}</b> was charged at <b>{effRate(invoice)}%</b>. A {delta < 0 ? "credit" : "debit"} note is issued on both the buyer invoice and the supplier invoice for this order. The taxable value stays the same; only the tax difference is adjusted.
        </p>
        <label style={{fontSize:12,color:"#6b7280"}}>Correct GST rate
          <select data-testid="gst-fix-rate" value={rate} onChange={e => setRate(Number(e.target.value))} style={inp}>
            {RATES.filter(r => r !== effRate(invoice)).map(r => <option key={r} value={r}>{r}%</option>)}
          </select>
        </label>
        <label style={{fontSize:12,color:"#6b7280"}}>Reason (printed on the note)
          <textarea data-testid="gst-fix-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Magazine printing is 5% (SAC 998912), invoiced at 18% by mistake" style={inp}/>
        </label>
        <div data-testid="gst-fix-preview" style={{fontSize:12.5,background: delta < 0 ? "#ECFDF5" : "#FEF3C7",padding:10,borderRadius:8}}>
          Taxable {money(taxable)} · {effRate(invoice)}% → {rate}% · <b>{delta < 0 ? "Credit" : "Debit"} {money(Math.abs(delta))}</b> on this invoice
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button className="secondary-btn" data-testid="gst-fix-cancel" onClick={close}>Cancel</button>
          <button className="primary-btn" data-testid="gst-fix-submit" style={{background:"#1E3A8A"}} disabled={busy || reason.trim().length < 5} onClick={submit}>
            {busy ? "Issuing…" : `Issue ${delta < 0 ? "credit" : "debit"} notes`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SettleCell({ note, onDone }) {
  const [ref, setRef] = useState("");
  if (note.settlement_status === "settled")
    return <span data-testid={`cn-settled-${note.id}`} style={{fontSize:11,color:"#065F46"}}>Settled · {note.settlement_reference}</span>;
  const settle = async () => {
    try {
      await axios.post(`${API}/admin/credit-notes/${note.id}/settle`, { reference: ref });
      toast.success("Marked settled");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark settled"); }
  };
  return (
    <span style={{display:"inline-flex",gap:4}}>
      <input data-testid={`cn-ref-${note.id}`} value={ref} onChange={e => setRef(e.target.value)} placeholder="Refund / UTR ref" style={{...inp,marginTop:0,width:130,padding:"4px 8px",fontSize:11}}/>
      <button data-testid={`cn-settle-${note.id}`} className="secondary-btn" disabled={ref.trim().length < 2} onClick={settle} style={{fontSize:11,padding:"4px 10px"}}>Settle</button>
    </span>
  );
}

export function CreditNotesPanel({ refresh }) {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/admin/credit-notes`).then(r => setData(r.data)).catch(() => setData({ notes: [], totals: {} }));
  useEffect(() => { load(); }, [refresh]);
  if (!data) return null;
  const t = data.totals;
  return (
    <div data-testid="credit-notes-panel" style={{marginTop:28}}>
      <h3 style={{margin:"0 0 4px"}}>Credit & debit notes</h3>
      <p style={{fontSize:12,color:"#64748b",margin:"0 0 12px"}}>GST-rate corrections issued u/s 34. A buyer credit means a refund is owed to the buyer. A supplier credit is already adjusted in pending settlements; for settlements already paid, it's recorded as an adjustment due.</p>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:12}}>
        <StatPill label="Notes" value={t.count || 0} testid="cn-count"/>
        <StatPill label="Open" value={t.open_count || 0} tone="warn" testid="cn-open"/>
        <StatPill label="Buyer credit" value={money(t.buyer_credit_inr)} testid="cn-buyer-credit"/>
        <StatPill label="Buyer debit" value={money(t.buyer_debit_inr)} testid="cn-buyer-debit"/>
        <StatPill label="Supplier credit" value={money(t.supplier_credit_inr)} testid="cn-sup-credit"/>
        <StatPill label="Supplier debit" value={money(t.supplier_debit_inr)} testid="cn-sup-debit"/>
      </div>
      {data.notes.length === 0 ? <div className="pipeline-empty" data-testid="cn-empty">No credit or debit notes issued yet.</div> : (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",fontSize:12,borderCollapse:"collapse"}}>
            <thead><tr style={{background:"#f3f4f6",textAlign:"left"}}>
              <th style={th}>Note #</th><th style={th}>Against invoice</th><th style={th}>Party</th><th style={th}>Rate change</th>
              <th style={{...th,textAlign:"right"}}>Tax difference</th><th style={th}>Reason</th><th style={th}>Settlement</th><th style={th}>Action</th>
            </tr></thead>
            <tbody>
              {data.notes.map(n => (
                <tr key={n.id} data-testid={`cn-row-${n.id}`} style={{borderBottom:"1px solid #e5e7eb"}}>
                  <td style={td}><b>{n.note_number}</b><div style={{fontSize:10,color:"#9ca3af"}}>{n.note_date?.slice(0,10)}</div></td>
                  <td style={td}>{n.original_invoice_number}</td>
                  <td style={td}>{n.invoice_type === "platform_to_customer" ? `Buyer · ${n.buyer?.name || "—"}` : `Supplier · ${n.seller?.name || "—"}`}</td>
                  <td style={td}>{n.old_gst_pct}% → {n.new_gst_pct}%</td>
                  <td style={{...td,textAlign:"right",fontWeight:600,color: n.note_type === "credit" ? "#065F46" : "#B45309"}}>{n.note_type === "credit" ? "−" : "+"}{money(n.gst_amount_inr)}</td>
                  <td style={{...td,maxWidth:220}}>{n.reason}</td>
                  <td style={td}><SettleCell note={n} onDone={load}/></td>
                  <td style={td}><a data-testid={`cn-pdf-${n.id}`} href={`${API}/credit-notes/${n.id}/pdf`} target="_blank" rel="noopener noreferrer" className="primary-btn" style={{textDecoration:"none",fontSize:11,padding:"4px 12px"}}>PDF</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
